import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Kicker, ArrowCta } from "@/components/Reveal";
import { COME_STEPS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export const Method = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 65%"] });
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="come" data-testid="method-section" className="relative overflow-hidden bg-navy bg-grid-dark py-24 text-white lg:py-36">
      <div ref={ref} className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Kicker dark>Come lo facciamo</Kicker>
          <h2 className="mt-5 max-w-4xl font-display text-4xl font-black leading-[1.0] tracking-tight sm:text-6xl">
            NON PARTIAMO DA UN PACCHETTO. <span className="text-brand-gradient">PARTIAMO DA TE.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
            Ogni azienda ha obiettivi, risorse, esigenze e tempi differenti. Per questo non vendiamo pacchetti prestabiliti: studiamo la situazione e costruiamo insieme la strategia più adatta.
          </p>
        </Reveal>

        <div className="relative mt-20">
          <svg viewBox="0 0 1200 60" fill="none" className="absolute -top-8 left-0 hidden w-full lg:block" aria-hidden="true">
            <defs>
              <linearGradient id="method-g" x1="0" y1="0" x2="1200" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#2563EB" />
                <stop offset="0.5" stopColor="#38BDF8" />
                <stop offset="1" stopColor="#FF8A00" />
              </linearGradient>
            </defs>
            <motion.path d="M 10 45 C 300 55, 500 20, 760 25 C 950 28, 1100 18, 1185 10" stroke="url(#method-g)" strokeWidth="2.5" strokeLinecap="round" style={{ pathLength }} />
            <motion.path d="M 1185 10 L 1165 6 M 1185 10 L 1178 28" stroke="url(#method-g)" strokeWidth="2.5" strokeLinecap="round" style={{ pathLength }} />
          </svg>

          <ol className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5">
            {COME_STEPS.map((step, i) => (
              <motion.li
                key={step.number}
                data-testid={`method-step-${step.number}`}
                className="relative border-t border-white/10 pt-8"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="absolute -top-[5px] left-0 h-2.5 w-2.5 rounded-full bg-brand-gradient" aria-hidden="true" />
                <span className="font-display text-4xl font-black text-outline-dark">{step.number}</span>
                <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{step.text}</p>
              </motion.li>
            ))}
          </ol>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 grid grid-cols-1 gap-10 border border-white/10 bg-white p-8 text-navy sm:p-12 lg:grid-cols-2 lg:gap-14" data-testid="consultation-card">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-4 py-1.5 font-display text-[11px] font-bold tracking-[0.18em] text-white">
                VALORE DICHIARATO: 80 €
              </span>
              <h3 className="mt-5 font-display text-3xl font-black leading-[1.05] tracking-tight sm:text-4xl">
                UNA CONSULENZA STRATEGICA DEL VALORE DI 80 €. <span className="text-brand-gradient">PER TE, GRATUITA.</span>
              </h3>
            </div>
            <div>
              <p className="text-base leading-relaxed text-slate-600">
                Durante la consulenza analizziamo il tuo punto di partenza e condividiamo una prima direzione strategica: opportunità, priorità e possibili interventi.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">
                Non è una promessa di risultati garantiti né un obbligo ad acquistare i nostri servizi. È un'occasione per comprendere meglio il mercato e capire quali scelte possano avere senso per la tua attività.
              </p>
              <div className="mt-7">
                <ArrowCta testId="consultation-cta" onClick={() => scrollToSection("contatti")}>
                  PRENOTA LA TUA CONSULENZA GRATUITA
                </ArrowCta>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 max-w-3xl border-l-4 border-brand-cyan pl-6 sm:pl-8" data-testid="pricing-block">
            <h3 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">QUANTO COSTA LAVORARE CON NOI?</h3>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-white/60">
              <p>Non esiste un prezzo valido per tutte le aziende, perché non esistono due progetti identici. Dopo aver compreso le tue esigenze, ti presentiamo ciò che riteniamo ideale per i tuoi obiettivi e definiamo insieme priorità e attività in base alle risorse disponibili.</p>
              <p>Il nostro approccio non cambia in base al budget: puntiamo sempre al miglior lavoro possibile. Cambiano l'ampiezza del progetto, le risorse impiegate, i tempi e le attività concretamente realizzabili.</p>
              <p className="font-semibold text-white">Il nostro obiettivo non è farti spendere di più. È aiutarti a utilizzare al meglio ciò che scegli di investire.</p>
              <p className="font-mono text-xs tracking-[0.25em] text-white/40">NESSUN LISTINO BASIC, STANDARD O PREMIUM.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
